import { BellOutlined } from "@ant-design/icons"
import { Badge, Button, Popover, Skeleton } from "antd"
import React, { Suspense, lazy, useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { type ConnectedProps, connect } from "react-redux"

import { RoutesPaths } from "../../../constants/routes"
import { useAppDispatch } from "../../../hooks/use-app-dispatch.ts"
import { useAppSelector } from "../../../hooks/use-app-selector.ts"
import { fetchNotificationCounts } from "../../../redux/notificationCounts/notification-counts.thunks.ts"
import type { RootState } from "../../../store.ts"
import { showNotification } from "../../../utils/notification/show-notification"
import { NOTIFICATION_PATHS } from "../api/services"
import { isNotificationClickable } from "../helpers/isNotificationClickable.ts"
import { parseNotificationMessage } from "../helpers/parseNotificationMessage.ts"
import {
  deleteNotifications,
  markAsRead,
  setConnected,
  setHasNewNotification,
  setInitialNotifications,
  setUnreadCount
} from "../redux/notification.slice.ts"
import {
  NOTIFICATION_TYPE,
  type Notification,
  WS_MESSAGE_TYPE
} from "../types/notification.types.ts"
import { NotificationsContent } from "./NotificationsContent.tsx"
import { NotificationsPermissionsAlert } from "./NotificationsPermissionsAlert.tsx"
import { NotificationsPopoverHeader } from "./NotificationsPopoverHeader.tsx"

const NotificationsModal = lazy(() => import("./NotificationsModal.tsx"))

interface NotificationsController extends ConnectedProps<typeof connector> {
  trigger?: (unreadCount: number) => React.ReactNode
}

function NotificationsController({
  notificationsInitial,
  unreadCount,
  hasNewNotification,
  accessToken,
  setConnected,
  setInitialNotifications,
  setHasNewNotification,
  markAsRead,
  deleteNotifications,
  trigger,
  hasMoreInitial,
  nextInitial,
  setUnreadCount,
  isConnected,
  isMobile
}: NotificationsController) {
  const wsRef = useRef<WebSocket | null>(null)
  const reset = useRef(true)

  // Separate from the WS feed above: Projects/Calendar unseen counts are
  // themselves sourced from that same notification stream (PROJECT_CREATED /
  // CALENDAR_EVENT_ASSIGNED are ordinary Notification rows, already inside
  // `unreadCount`), so adding them again here would double-count. Approvals
  // (pending proposals/contracts/document reviews) are NOT notification
  // events — they are derived, ongoing record state — so they are the one
  // addition the bell needs. Fetched once here, the one place mounted for
  // every authenticated page, and shared via redux with the sidebar/Home
  // cards. See docs/kb/decisions/2026-09-12-notification-badges-phase-1.md.
  const dispatch = useAppDispatch()
  const approvalsTotal = useAppSelector(
    (state) => state.notificationCounts.approvals.total
  )

  useEffect(() => {
    dispatch(fetchNotificationCounts())
  }, [dispatch])

  const [isPopoverOpen, setIsPopoverOpen] = useState(false)
  const [isModalOpen, setIsModalOpen] = useState(false)

  const [notifications, setNotifications] = useState<Notification[]>([])
  const [hasMore, setHasMore] = useState(true)
  const [next, setNext] = useState<number | null>(null)
  const [fetching, setIsFetching] = useState(false)
  const [permissionsNotification, setPermissionsNotification] =
    useState<Notification | null>(null)

  useEffect(() => {
    if (reset.current) {
      setNotifications(notificationsInitial)
      setHasMore(hasMoreInitial)
      setNext(nextInitial)
    } else {
      reset.current = true
    }
  }, [
    notificationsInitial,
    hasMoreInitial,
    nextInitial,
    isPopoverOpen,
    isModalOpen
  ])

  useEffect(() => {
    if (!accessToken) return

    // Heartbeat keeps the socket under the ALB's 60s idle timeout.
    const HEARTBEAT_MS = 25000
    const RECONNECT_BASE_MS = 1000
    const RECONNECT_MAX_MS = 30000
    let heartbeat: ReturnType<typeof setInterval> | undefined
    let reconnectTimer: ReturnType<typeof setTimeout> | undefined
    let reconnectAttempts = 0
    let closedByCleanup = false

    const connect = () => {
      const ws = new WebSocket(
        NOTIFICATION_PATHS.getNotifications(accessToken)
      )
      wsRef.current = ws

      ws.onopen = () => {
        setConnected(true)
        reconnectAttempts = 0
        heartbeat = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) {
            ws.send(JSON.stringify({ type: WS_MESSAGE_TYPE.PING }))
          }
        }, HEARTBEAT_MS)
      }

      ws.onclose = () => {
        if (heartbeat) clearInterval(heartbeat)
        // Ignore a stale socket's close (a remount already replaced it).
        if (closedByCleanup || ws !== wsRef.current) return
        setConnected(false)
        const backoff = Math.min(
          RECONNECT_BASE_MS * 2 ** reconnectAttempts,
          RECONNECT_MAX_MS
        )
        reconnectAttempts += 1
        reconnectTimer = setTimeout(connect, backoff + Math.random() * 1000)
      }

      ws.onmessage = (event) => {
        const data = parseNotificationMessage(event.data)
        if (!data) return

        if (
          (data.type === WS_MESSAGE_TYPE.INITIAL_STATE ||
            data.type === WS_MESSAGE_TYPE.GET_ALL_MESSAGES) &&
          data.notifications
        ) {
          setInitialNotifications({
            notifications: data.notifications,
            unreadCount: data.unread_count || 0,
            hasMore: data.has_more || false,
            next: data.next_cursor
          })
          setIsFetching(false)
        }

        if (data.type === WS_MESSAGE_TYPE.NOTIFICATION_NEW && data.notification) {
          setHasNewNotification(true)
        }

        if (data.type === WS_MESSAGE_TYPE.NOTIFICATIONS_BATCH) {
          if (data.notifications) {
            setNotifications((prev) => [
              ...prev,
              ...(data.notifications as Notification[])
            ])
          }

          setHasMore(data.has_more || false)
          setNext(data.next_cursor)
        }

        // Server is authoritative; syncing here prevents a stuck optimistic count.
        if (
          (data.type === WS_MESSAGE_TYPE.MARK_READ_SUCCESS ||
            data.type === WS_MESSAGE_TYPE.BADGE_UPDATE) &&
          typeof data.unread_count === "number"
        ) {
          setUnreadCount(data.unread_count)
        }

        if (data.type === WS_MESSAGE_TYPE.ERROR) {
          setIsFetching(false)
        }

        if (
          data.type === WS_MESSAGE_TYPE.NOTIFICATION_NEW &&
          data.notification?.type === NOTIFICATION_TYPE.PERMISSIONS &&
          data?.notification?.unread
        ) {
          setPermissionsNotification(data.notification)
        }
      }
    }

    connect()

    return () => {
      closedByCleanup = true
      if (heartbeat) clearInterval(heartbeat)
      if (reconnectTimer) clearTimeout(reconnectTimer)
      wsRef.current?.close()
      // Reset here too: onclose returns early when closedByCleanup is set.
      setConnected(false)
    }
  }, [
    accessToken,
    setConnected,
    setInitialNotifications,
    setHasNewNotification,
    setUnreadCount
  ])

  const { t } = useTranslation()

  const sendWhenOpen = (payload: Record<string, unknown>): boolean => {
    if (wsRef.current?.readyState !== WebSocket.OPEN) {
      showNotification.warning({
        message: t("notifications.bell.actionUnavailable")
      })
      return false
    }

    wsRef.current.send(JSON.stringify(payload))
    return true
  }

  const handleMarkAsRead = (notificationId: number | null) => {
    reset.current = false

    if (
      !sendWhenOpen({
        type: WS_MESSAGE_TYPE.MARK_READ,
        notification_id: notificationId
      })
    ) {
      reset.current = true
      return
    }

    markAsRead(notificationId)
    if (notificationId === null) {
      setNotifications((prev) => prev.map((n) => ({ ...n, unread: false })))
    } else {
      setNotifications((prev) =>
        prev.map((n) =>
          n.id === notificationId ? { ...n, unread: false } : n
        )
      )
    }
  }

  const onLoadMore = () => {
    sendWhenOpen({ type: WS_MESSAGE_TYPE.FETCH_MORE, after_id: next })
  }

  const handleFetchNotifications = () => {
    setIsFetching(true)
    if (!sendWhenOpen({ type: WS_MESSAGE_TYPE.GET_ALL_MESSAGES })) {
      setIsFetching(false)
      return
    }
    setHasNewNotification(false)
  }

  const handleDelete = (notificationId: number | null) => {
    reset.current = false

    if (!sendWhenOpen({ type: WS_MESSAGE_TYPE.DELETE, id: notificationId })) {
      reset.current = true
      return
    }

    deleteNotifications(notificationId)
    if (notificationId === null) {
      setNotifications([])
      return
    }

    // Count removed-unread first; the setState updater would read a stale unreadCount.
    const removedUnread = notifications.filter(
      (n) => n.id === notificationId && n.unread
    ).length

    setNotifications((prev) => prev.filter((n) => n.id !== notificationId))

    if (removedUnread > 0) {
      setUnreadCount(Math.max(0, unreadCount - removedUnread))
    }
  }

  const handleCardClick = (notification: Notification) => {
    if (!isNotificationClickable(notification)) return

    const target = notification?.target

    switch (target?.type) {
      case "project":
        window.open(RoutesPaths.project.view(target.id).overview, "_blank")
        break
      case "lead":
        window.open(RoutesPaths.lead.view(target.id), "_blank")
        break
    }
  }

  const openModal = () => {
    setIsPopoverOpen(false)
    setIsModalOpen(true)
  }

  const bellCount = unreadCount + approvalsTotal
  const bell = trigger ? (
    trigger(unreadCount)
  ) : (
    <Badge
      count={bellCount}
      offset={bellCount > 9 ? [-8, 2] : [-4, 2]}
      className="z-10"
    >
      <Button
        type="text"
        icon={<BellOutlined />}
        className="shadow-sm border border-gray-100"
        size="large"
      />
    </Badge>
  )

  return (
    <>
      {permissionsNotification && (
        <Suspense>
          <NotificationsPermissionsAlert
            permissionsNotification={permissionsNotification}
            onMarkAsRead={handleMarkAsRead}
            onPermissionsNotification={() => setPermissionsNotification(null)}
          />
        </Suspense>
      )}
      {isMobile ? (
        // The popover is right-aligned to a mid-header trigger, so it cannot fit a phone.
        <span className="inline-flex" onClick={() => setIsModalOpen(true)}>
          {bell}
        </span>
      ) : (
        <Popover
          trigger="click"
          open={isPopoverOpen}
          onOpenChange={(value) => setIsPopoverOpen(value)}
          placement="bottomRight"
          title={
            <NotificationsPopoverHeader
              unreadCount={unreadCount}
              onMarkAllAsRead={() => handleMarkAsRead(null)}
              onExpand={openModal}
              disabled={!isConnected}
            />
          }
          content={
            <NotificationsContent
              notifications={notifications}
              hasNewNotification={hasNewNotification}
              onMarkAsRead={handleMarkAsRead}
              onCardClick={handleCardClick}
              onDelete={handleDelete}
              hasMore={hasMore}
              onLoadMore={onLoadMore}
              refetchNotifications={handleFetchNotifications}
              fetching={fetching}
            />
          }
        >
          {bell}
        </Popover>
      )}

      {isModalOpen && (
        <Suspense
          fallback={
            <Skeleton.Node
              active
              className={"fixed inset-0 z-10  m-auto h-[90vh] w-[90vw]"}
            />
          }
        >
          <NotificationsModal
            open={isModalOpen}
            onClose={() => setIsModalOpen(false)}
            notifications={notifications}
            unreadCount={unreadCount}
            onMarkAsRead={handleMarkAsRead}
            onCardClick={handleCardClick}
            onDelete={handleDelete}
            hasMore={hasMore}
            onLoadMore={onLoadMore}
            refetchNotifications={handleFetchNotifications}
            hasNewNotification={hasNewNotification}
            fetching={fetching}
            disabled={!isConnected}
            isMobile={isMobile}
          />
        </Suspense>
      )}
    </>
  )
}

const mapStateToProps = (state: RootState) => ({
  accessToken: state.auth.accessToken,
  notificationsInitial: state.notification.notifications,
  unreadCount: state.notification.unreadCount,
  hasNewNotification: state.notification.hasNewNotification,
  hasMoreInitial: state.notification.hasMore,
  nextInitial: state.notification.next,
  isConnected: state.notification.isConnected,
  isMobile: state.nav.isMobile
})

const mapDispatchToProps = {
  markAsRead,
  setConnected,
  setHasNewNotification,
  setInitialNotifications,
  deleteNotifications,
  setUnreadCount
}

const connector = connect(mapStateToProps, mapDispatchToProps)
export default connector(NotificationsController)
